import { useState, useEffect, useContext, lazy, Suspense, useMemo, useCallback } from 'react';
import { useParams } from "react-router-dom";

// @material-ui/core (v4)
import { Tabs, Tab, Box, Button } from '@material-ui/core';

// @mui/material (v5)
import { CircularProgress, Tooltip, Menu, MenuItem, IconButton, Chip, Snackbar } from '@mui/material';

// @mui/icons-material
import { Add, MoreVert } from '@mui/icons-material';

// DataGrid
import { DataGrid, useGridApiContext, gridFilterStateSelector } from '@mui/x-data-grid';

// Internal imports
import AuthContext from '../../store/AuthContext.js';
import ModalContext from '../../store/ModalContext.js';
import DrawerContext from "../../store/DrawerContext.js";
import DataGridContext from '../../store/DataGridContext';
import ManageModulesAndLessons from '../../Components/Course/ManageModulesAndLessons.jsx';
import Arrowleft from "../../assets/img/Course/arrow-left.svg"
import CreateLesson from '../../Components/Course/CreateLesson.jsx';
import CouponsTab from '../AssessmentUsers/AssessmentUsers/tabs/CouponsTab.jsx';

const truncateCourseTitle = (title, maxLen = 40) => {
    if (!title) return '';
    const dupMatch = title.match(/^(.+?)\s*(\(\d+\))$/);
    if (dupMatch) {
        const base = dupMatch[1];
        const suffix = ` ${dupMatch[2]}`;
        const allowedBase = maxLen - suffix.length;
        const truncatedBase = base.length > allowedBase ? base.slice(0, allowedBase).trimEnd() + '...' : base;
        return truncatedBase + suffix;
    }
    return title.length > maxLen ? title.slice(0, maxLen).trimEnd() + '...' : title;
};

const AssessmentDashboardPage = lazy(() => import('../AssessmentDashboardPage'));


const DataGridState = () => {
    const apiRef = useGridApiContext();
    const datagridCtx = useContext(DataGridContext);
    const filterState = gridFilterStateSelector(apiRef.current.state);

    useEffect(() => {
        let filteredIDs = filterState?.filteredRowsLookup;
        let filteredUserIDsArr = [];
        for (let key in filteredIDs) {
            if (filteredIDs[key] === true) {
                filteredUserIDsArr.push(parseInt(key));
            }
        }
        datagridCtx.updateFilteredDataGridUsers(filteredUserIDsArr);
    }, [filterState, datagridCtx.tab]);

    return null;
};


function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}



const CourseManager = () => {

    const { id } = useParams();
    const authCtx = useContext(AuthContext);
    const modalCtx = useContext(ModalContext);
    const drawerCtx = useContext(DrawerContext);
    const datagridCtx = useContext(DataGridContext);

    const [tabValue, setTabValue] = useState(() => {
        try {
            const saved = sessionStorage.getItem(`courseManager_tabValue_${id}`);
            return saved ? JSON.parse(saved) : 0;
        } catch (e) {
            return 0;
        }
    });
    const [datagridPage, setDatagridPage] = useState(0);
    const [selectionModel, setSelectionModel] = useState([]);
    const [isExportingActive, setIsExportingActive] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeRowId, setActiveRowId] = useState(null);
    const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
    const [assessDetails, setAssessDetails] = useState(null);
    const [activeView, setActiveView] = useState(() => {
        try {
            const saved = sessionStorage.getItem(`courseManager_activeView_${id}`);
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            return null;
        }
    });
    const [hideTabContainer, setHideTabContainer] = useState(() => {
        try {
            const saved = sessionStorage.getItem(`courseManager_hideTabContainer_${id}`);
            return saved ? JSON.parse(saved) : false;
        } catch (e) {
            return false;
        }
    });

    useEffect(() => {
        sessionStorage.setItem(`courseManager_tabValue_${id}`, JSON.stringify(tabValue));
    }, [tabValue, id]);

    useEffect(() => {
        if (activeView) {
            sessionStorage.setItem(`courseManager_activeView_${id}`, JSON.stringify(activeView));
        } else {
            sessionStorage.removeItem(`courseManager_activeView_${id}`);
        }
    }, [activeView, id]);

    useEffect(() => {
        sessionStorage.setItem(`courseManager_hideTabContainer_${id}`, JSON.stringify(hideTabContainer));
    }, [hideTabContainer, id]);
    const [courses, setCourses] = useState([]);

    // Helper to always go back to manage-modules with all required fields
    const goToManageModules = useCallback((course, assessmentId, expandedModuleId = null) => {
        setActiveView({ type: 'manage-modules', course, assessmentId, expandedModuleId });
    }, []);

    const handleMenuOpen = useCallback((event, rowId) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setActiveRowId(rowId);
    }, []);

    const fetchAssessmentDetails = useCallback(() => {
        fetch(`${process.env.REACT_APP_API_URI}/v1/admin/assessment/${id}`, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authCtx.token
            },
        })
            .then(response => response.json())
            .then(data => {
                if (data.status === 'success') {
                    setAssessDetails(data.details);
                }
            })
            .catch((err) => {
                console.error('Failed to fetch assessment details...', err);
            });
    }, [id, authCtx.token]);

    const fetchCourses = useCallback(() => {
        fetch(`${process.env.REACT_APP_API_URI}` + `/v8/admin/assessment/${id}/courses`, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authCtx.token
            },
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                if (data.statusText === 'Success') {
                    const mapped = data.data.courses.map(course => ({
                        id: course.id,
                        name: course.title,
                        description: course.description,
                        thumbnail_url: course.thumbnail_url,
                        date: course.created_at?.split(' ')[0],
                        created_at: course.created_at,
                        status: course.is_active === 1 ? 'Active' : 'Inactive',
                        price: course.price,
                        sales_pitch: course.sales_pitch,
                        intro_text: course.intro_text,
                        is_in_bundle: course.is_in_bundle,
                    }));
                    setCourses(mapped);
                }
            })
            .catch((err) => {
                console.error('Failed to fetch courses...', err);
            });
    }, [id, authCtx.token]);

    const duplicateCourse = useCallback((courseId) => {
        fetch(`${process.env.REACT_APP_API_URI}/v8/admin/assessment/${id}/courses/${courseId}/duplicate`, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authCtx.token
            },
        })
            .then(response => {
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                return response.json();
            })
            .then(data => {
                if (data.statusText === 'Success') fetchCourses();
            })
            .catch((err) => console.error('Failed to duplicate course...', err));
    }, [id, authCtx.token, fetchCourses]);

    const copyCourseLink = useCallback(async (courseId) => {
        if (!courseId) return;
        const baseUrl = (assessDetails?.url || '').replace(/\/$/, '');
        if (!baseUrl) {
            console.error('Assessment URL is not available');
            return;
        }
        const link = `${baseUrl}/course-purchase/pricing?courseId=${courseId}`;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(link);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = link;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopySnackbarOpen(true);
        } catch (err) {
            console.error('Failed to copy course link...', err);
        }
    }, [assessDetails?.url]);

    useEffect(() => {
        fetchAssessmentDetails();
        fetchCourses();
    }, [fetchAssessmentDetails, fetchCourses]);

    const columns = useMemo(() => [
        { field: 'created_at', hide: true },
        {
            field: 'name', headerName: 'Course Name', flex: 1.5, minWidth: 200,
            renderCell: (params) => {
                const title = params.value || '';
                const truncatedText = truncateCourseTitle(title, 20);
                const isTruncated = title !== truncatedText;

                const content = (
                    <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {truncatedText}
                    </span>
                );

                return isTruncated ? (
                    <Tooltip
                        title={title}
                        placement="top"
                        arrow
                        componentsProps={{
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, -4],
                                        },
                                    },
                                ],
                            },
                        }}
                    >
                        {content}
                    </Tooltip>
                ) : content;
            }
        },
        {
            field: 'intro_text', headerName: 'Intro Text', flex: 2, minWidth: 240,
            renderCell: (params) => {
                const rawIntro = params.value || '';
                const intro = rawIntro.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                const isTruncated = intro.length > 27;
                const truncatedText = isTruncated ? intro.slice(0, 27).trimEnd() + '...' : intro;

                const content = (
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {truncatedText}
                    </span>
                );

                return isTruncated ? (
                    <Tooltip
                        title={intro}
                        placement="top"
                        arrow
                        componentsProps={{
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, -4],
                                        },
                                    },
                                ],
                            },
                        }}
                    >
                        {content}
                    </Tooltip>
                ) : content;
            }
        },
        { field: 'date', headerName: 'Date Created', flex: 1.2, minWidth: 180 },
        {
            field: 'status', headerName: 'Status', flex: 1, minWidth: 120,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    sx={{
                        color: params.value === 'Active' ? '#008000' : '#FF3B30',
                        backgroundColor: 'transparent',
                    }}
                />
            )
        },
        {
            field: 'price', headerName: 'Price', flex: 1, minWidth: 120,
            renderCell: (params) => `$${params.value}`
        },
        {
            field: 'actions', headerName: 'Actions', flex: 0.8, minWidth: 100,
            sortable: false, disableColumnMenu: true,
            align: 'center', headerAlign: 'center',
            renderCell: (params) => (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, params.row.id); }}>
                    <MoreVert fontSize="small" />
                </IconButton>
            )
        },
    ], [handleMenuOpen]);

    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveRowId(null);
    };

    const handleTabChange = (event, newValue) => {
        setSelectionModel([]);
        setTabValue(newValue);
    };

    return (
        <>
            <div className="assessment-page">
                <div className="assessment-name">
                    {(!activeView || tabValue !== 0) &&
                        <div data-aos="fade-right">
                            <h1><a style={{ textDecoration: "none", fontSize: "32px" }}>{assessDetails?.title || 'Course Manager'}</a></h1>
                            {assessDetails?.url && (
                                <p className="assessment-link-text">
                                    Link to assessment:{' '}
                                    <a className="assessment-link" href={assessDetails.url} target="_blank" rel="noopener noreferrer">
                                        {assessDetails.url}
                                    </a>
                                </p>
                            )}
                        </div>
                    }
                    {(activeView?.type === 'manage-modules' && tabValue === 0) && (
                        <div className='back-to-course-container' onClick={() => setActiveView(null)}>
                            <img src={Arrowleft} alt="Arrowleft" />
                            <div className='back-to-course'>Back to Courses</div>
                        </div>
                    )}
                    {(activeView?.type === 'create-lesson' && tabValue === 0) && (
                        <div className='back-to-course-container' onClick={() => goToManageModules(activeView.course, activeView.assessmentId, activeView.module?.id)}>
                            <img src={Arrowleft} alt="Arrowleft" />
                            <div className='back-to-course'>Back to Modules</div>
                        </div>
                    )}
                </div>
                {activeView?.type === 'create-lesson'
                    ? <CreateLesson
                        module={activeView.module}
                        lesson={activeView.lesson}
                        assessmentId={activeView.assessmentId}
                        courseId={activeView.course?.id}
                        onBack={() => goToManageModules(activeView.course, activeView.assessmentId, activeView.module?.id)}
                        onLessonSaved={(updatedLesson) => setActiveView(prev => ({ ...prev, lesson: updatedLesson }))}
                    />
                    : !hideTabContainer &&
                    <div className="tab-container">
                        <Box sx={{ width: '100%' }}>
                            <Box className="box" sx={{ borderBottom: 1, borderColor: '#EDEDED' }}>
                                <Tabs
                                    TabIndicatorProps={{ style: { background: '#F2D466', height: '4px' } }}
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    aria-label="course manager tabs"
                                >
                                    <Tab className="tab-title" label={activeView?.type === 'manage-modules' ? "Module Manager" : "Course Manager"} {...a11yProps(0)} />
                                    <Tab className="tab-title" label="Coupons" {...a11yProps(1)} />
                                </Tabs>
                            </Box>
                            {activeView?.type === 'manage-modules' && tabValue === 0
                                ? <ManageModulesAndLessons
                                    course={activeView.course}
                                    assessmentId={activeView.assessmentId}
                                    expandedModuleId={activeView.expandedModuleId}
                                    onBack={() => setActiveView(null)}
                                    onViewChange={(isDeep) => setHideTabContainer(isDeep)}
                                    onAddLesson={(module) => setActiveView({ type: 'create-lesson', module, course: activeView.course, assessmentId: activeView.assessmentId })}
                                    onEditLesson={(module, lesson) => setActiveView({ type: 'create-lesson', module, lesson, course: activeView.course, assessmentId: activeView.assessmentId })}
                                />
                                :
                                <>
                                    {tabValue === 0 && (
                                        <Button
                                            className="add-course-cta"
                                            style={{ backgroundColor: '#2C3E50' }}
                                            onClick={() => {
                                                modalCtx.setDetails("add-course-modal", {
                                                    id,
                                                    mode: 'add',
                                                    refreshCourses: fetchCourses
                                                });
                                                modalCtx.openModal();
                                            }}
                                        >
                                            <Add /> ADD COURSE
                                        </Button>
                                    )}

                                    <TabPanel value={tabValue} index={0} className="course-manage-table">
                                        <DataGrid
                                            autoHeight
                                            initialState={{ sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] } }}
                                            rows={courses}
                                            columns={columns}
                                            components={{ Toolbar: DataGridState }}
                                            pagination
                                            rowCount={courses.length}
                                            pageSize={5}
                                            page={datagridPage}
                                            onPageChange={(newPage) => setDatagridPage(newPage)}
                                            checkboxSelection
                                            selectionModel={selectionModel}
                                            onSelectionModelChange={(newSelectionModel) => {
                                                setIsExportingActive(newSelectionModel.length > 0);
                                                setSelectionModel(newSelectionModel);
                                            }}
                                            onRowClick={(params, event) => {
                                                if (
                                                    event.target.closest('.MuiDataGrid-cellCheckbox') ||
                                                    event.target.closest('[data-field="actions"]')
                                                ) {
                                                    return;
                                                }
                                                goToManageModules(params.row, id);
                                            }}
                                            sx={{
                                                '& .MuiDataGrid-virtualScroller': {
                                                    overflowY: 'hidden !important',
                                                },
                                                '& .MuiDataGrid-main': {
                                                    width: '100%',
                                                },
                                                '& .MuiDataGrid-virtualScrollerContent': {
                                                    width: '100% !important',
                                                },
                                                '& .MuiDataGrid-virtualScrollerRenderZone': {
                                                    width: '100% !important',
                                                },
                                                '& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
                                                    width: '24px !important',
                                                    minWidth: '24px !important',
                                                    maxWidth: '24px !important',
                                                },
                                                '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-checkboxInput, & .MuiDataGrid-cellCheckbox .MuiDataGrid-checkboxInput, & .MuiDataGrid-columnHeaderCheckbox .MuiIconButton-root, & .MuiDataGrid-cellCheckbox .MuiIconButton-root': {
                                                    visibility: 'hidden !important',
                                                },
                                                '& .MuiDataGrid-columnHeader .MuiDataGrid-iconSeparator': {
                                                    visibility: 'visible !important',
                                                    opacity: '1 !important',
                                                },
                                                '& .MuiDataGrid-columnHeader[data-field="actions"] .MuiDataGrid-columnSeparator': {
                                                    display: 'none !important',
                                                },
                                                '& .MuiDataGrid-cellContent': {
                                                    fontSize: '15px',
                                                    color: "#262626"
                                                },
                                                '& .MuiDataGrid-row': {
                                                    width: '100% !important',
                                                    minWidth: '100% !important',
                                                    boxSizing: 'border-box !important',
                                                    borderBottom: '1px solid #D4D4D4 !important',
                                                    cursor: 'pointer',
                                                },
                                                '& .MuiDataGrid-cell': {
                                                    borderBottom: 'none !important',
                                                },
                                                '& .MuiDataGrid-cell[data-field="actions"]': {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                },
                                            }}
                                            disableSelectionOnClick
                                            rowsPerPageOptions={[5]}
                                        />
                                    </TabPanel>

                                    <CouponsTab
                                        index={1}
                                        tabValue={tabValue}
                                        TabPanel={TabPanel}
                                        mode="course"
                                        assessmentId={id}
                                        courses={courses}
                                        selectionModel={selectionModel}
                                        setSelectionModel={setSelectionModel}
                                        isBulkDeleting={false}
                                        bulkDeleteCoupons={() => { }}
                                        DataGridState={DataGridState}
                                        datagridPage={datagridPage}
                                        setDatagridPage={setDatagridPage}
                                    />

                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl) && activeRowId !== null}
                                        onClose={handleMenuClose}
                                        keepMounted={false}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        PaperProps={{ className: 'course-action-menu' }}
                                    >
                                        <MenuItem onClick={() => {
                                            const course = courses.find(c => c.id === activeRowId);
                                            goToManageModules(course, id);
                                            handleMenuClose();
                                        }}>Manage Modules & Lessons</MenuItem>
                                        <MenuItem
                                            onClick={() => {
                                                const course = courses.find(c => c.id === activeRowId);
                                                modalCtx.setDetails("add-course-modal", {
                                                    id,
                                                    mode: 'edit',
                                                    course,
                                                    refreshCourses: fetchCourses
                                                });
                                                modalCtx.openModal();
                                                handleMenuClose();
                                            }}
                                        >Edit Course Details</MenuItem>

                                        <MenuItem onClick={() => {
                                            duplicateCourse(activeRowId);
                                            handleMenuClose();
                                        }}>Duplicate Course</MenuItem>
                                        <MenuItem onClick={() => {
                                            copyCourseLink(activeRowId);
                                            handleMenuClose();
                                        }}>Copy Course Link</MenuItem>
                                        <MenuItem onClick={() => {
                                            const course = courses.find(c => c.id === activeRowId);
                                            modalCtx.setDetails("delete-course-confirmation", {
                                                deleteUrl: `${process.env.REACT_APP_API_URI}/v8/admin/assessment/${id}/courses/${activeRowId}`,
                                                refreshCallback: fetchCourses,
                                                itemName: course?.name,
                                                itemType: 'course',
                                            });
                                            modalCtx.openModal();
                                            handleMenuClose();
                                        }}>Delete Course</MenuItem>
                                    </Menu>
                                </>
                            }
                        </Box>
                    </div>
                }
            </div>
            <Snackbar
                open={copySnackbarOpen}
                autoHideDuration={2000}
                onClose={() => setCopySnackbarOpen(false)}
                message="Course link copied"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
}

export default CourseManager;